import React from 'react';
import { observer } from 'mobx-react';
import classNames from 'classnames';

import { ButtonClickEvent } from 'ui/base/types';
import { Theme, ThemedStyles, useStyles } from 'ui/base/theme';

import { IssueKind } from 'services/pangolin/types';
import { getIssueKindTitle } from 'services/pangolin/logic/issues/issues';

import {
  themedIssueKindIconClassStyles,
  getIssueKindIcon,
} from 'assets/enums/issue_kinds/issue_kinds';

import baseStyles from './kind_entry.module.css';

type KindEntryStyles = {
  plainContainer: string;
  clickableContainer: string;
  iconContainer: string;
  detailContainer: string;
  detailText: string;
  bareButton: string;
  removeIcon: string;
  baseIcon: string;
};

const themedStyles: ThemedStyles<KindEntryStyles> = {
  [Theme.DAYLIGHT]: {
    plainContainer: baseStyles.plainContainer,
    clickableContainer: baseStyles.clickableContainer,
    iconContainer: baseStyles.iconContainer,
    detailContainer: baseStyles.detailContainer,
    detailText: baseStyles.detailText,
    bareButton: baseStyles.bareButton,
    removeIcon: baseStyles.midnightRemoveIcon,
    baseIcon: baseStyles.baseIcon,
  },
  [Theme.MIDNIGHT]: {
    plainContainer: baseStyles.plainContainer,
    clickableContainer: baseStyles.clickableContainer,
    iconContainer: baseStyles.iconContainer,
    detailContainer: baseStyles.detailContainer,
    detailText: baseStyles.detailText,
    bareButton: baseStyles.bareButton,
    removeIcon: baseStyles.midnightRemoveIcon,
    baseIcon: baseStyles.baseIcon,
  },
};

type RemovableKindEntryProps = {
  kind: IssueKind;
  onRemoveClick: (event: ButtonClickEvent) => void;
};

export const RemovableKindEntry = observer(({ kind, onRemoveClick }: RemovableKindEntryProps) => {
  const styles = useStyles(themedStyles);
  const issueIconClasses = useStyles(themedIssueKindIconClassStyles);
  const issueIconClass = getIssueKindIcon(issueIconClasses, kind);
  return (
    <div className={styles.plainContainer}>
      <div className={styles.iconContainer}>
        <div className={classNames(styles.baseIcon, issueIconClass)}/>
      </div>
      <div className={styles.detailContainer}>
        <div className={styles.detailText}>
          {getIssueKindTitle(kind)}
        </div>
        <button type='button' className={styles.bareButton} onClick={onRemoveClick}>
          <div className={styles.removeIcon}/>
        </button>
      </div>
    </div>
  );
});

export type SearchKindEntryProps = {
  data: IssueKind;
  onSelect: () => void;
};

export const SearchKindEntry = observer(({ data: kind, onSelect }: SearchKindEntryProps) => {
  const styles = useStyles(themedStyles);
  const issueIconClasses = useStyles(themedIssueKindIconClassStyles);
  const issueIconClass = getIssueKindIcon(issueIconClasses, kind);
  return (
    <div className={styles.clickableContainer} onClick={onSelect}>
      <div className={styles.iconContainer}>
        <div className={classNames(styles.baseIcon, issueIconClass)}/>
      </div>
      <div className={styles.detailContainer}>
        <div className={styles.detailText}>
          {getIssueKindTitle(kind)}
        </div>
      </div>
    </div>
  );
});

export type PlainKindEntryProps = {
  kind: IssueKind;
};

export const PlainKindFilter = observer(({ kind }: PlainKindEntryProps) => {
  const styles = useStyles(themedStyles);
  const issueIconClasses = useStyles(themedIssueKindIconClassStyles);
  const issueIconClass = getIssueKindIcon(issueIconClasses, kind);
  return (
    <div className={styles.plainContainer}>
      <div className={styles.iconContainer}>
        <div className={classNames(styles.baseIcon, issueIconClass)}/>
      </div>
      <div className={styles.detailContainer}>
        <div className={styles.detailText}>
          {getIssueKindTitle(kind)}
        </div>
      </div>
    </div>
  );
});
